import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { GlobalSearchTab } from '@plato/search/search-types';
import { SearchEntity } from '@plato/search/search-types';
import * as React from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SearchDialog } from '../../shared/components/search/search.tsx';
import { useSearchEntityNavigation, type SearchEntityItem } from '../../shared/hooks/use-search-entity.ts';
import { GlobalSearchContext, type GlobalSearchContextType } from './global-search-bar-context.tsx';
import { GlobalSearchContent } from './global-search-content.tsx';

export type Props = {
    onBeforeOpenSearch: () => void;
} & React.PropsWithChildren;

export function useGlobalSearch(): GlobalSearchContextType {
    const context = React.useContext(GlobalSearchContext);
    if (!context) {
        throw new Error('useGlobalSearch must be used within a GlobalSearchProvider');
    }
    return context;
}

export function GlobalSearchBarProvider({ children, onBeforeOpenSearch }: Props) {
    const { t } = useTranslation();
    const { enableGlobalSearchWithoutAllTab } = useAppConfig();
    const [open, setOpen] = React.useState(false);
    const [defaultTab, setDefaultTab] = React.useState<GlobalSearchTab | undefined>(undefined);
    const searchInputRef = React.useRef<HTMLInputElement>(null);

    const openSearch = (initialTab?: GlobalSearchTab): void => {
        setDefaultTab(initialTab);
        onBeforeOpenSearch();

        // Touch platforms only raise the software keyboard for a focus() that runs inside
        // the tap that asked for it, and base-ui resolves `initialFocus` a frame later — so
        // the field used to open with a caret and no keyboard. Committing the dialog
        // synchronously puts the field in the DOM in time to focus it while the tap still
        // counts; base-ui then re-focuses the field we already focused, which is a no-op.
        //
        // Without user activation there is no keyboard to raise (the TAPI page auto-opens
        // search from an effect), and flushSync during a commit only warns and defers.
        if (navigator.userActivation.isActive) {
            flushSync(() => {
                setOpen(true);
            });
            searchInputRef.current?.focus();
            return;
        }

        setOpen(true);
    };

    const { navigateToEntity } = useSearchEntityNavigation();

    React.useEffect(() => {
        const down = (e: KeyboardEvent): void => {
            if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || e.key === '/') {
                if (
                    (e.target instanceof HTMLElement && e.target.isContentEditable) ||
                    e.target instanceof HTMLInputElement ||
                    e.target instanceof HTMLTextAreaElement ||
                    e.target instanceof HTMLSelectElement
                ) {
                    return;
                }

                e.preventDefault();
                setOpen(true);
            }
        };

        document.addEventListener('keydown', down);

        return () => {
            document.removeEventListener('keydown', down);
        };
    }, []);

    const runCommand = React.useCallback((command: () => unknown) => {
        command();
        setOpen(false);
    }, []);

    const handleItemSelected = React.useCallback(({ id, entity }: SearchEntityItem) => {
        runCommand(() => {
            navigateToEntity({
                erp_id: id,
                entity: entity,
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <GlobalSearchContext.Provider value={{ openSearch, open }}>
            <SearchDialog open={open} onOpenChange={setOpen} title={t('top_navbar__search-placeholder')}>
                <GlobalSearchContent
                    inputRef={searchInputRef}
                    entities={[
                        SearchEntity.Customer,
                        SearchEntity.Product,
                        SearchEntity.SalesOrder,
                        SearchEntity.SalesQuote,
                    ]}
                    onItemSelected={handleItemSelected}
                    initialTab={defaultTab}
                    hideAllTab={enableGlobalSearchWithoutAllTab}
                    tracking={{ context: 'global' }}
                />
            </SearchDialog>
            {children}
        </GlobalSearchContext.Provider>
    );
}
